// src/pages/DashboardPage.js
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useMyProperties } from "../hooks/useProperties";
import { deleteProperty, updateProperty } from "../utils/api";
import Spinner from "../components/Spinner";
import toast from "react-hot-toast";

const fmt = (n) => {
  if (!n) return "—";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)} Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)} L`;
  return `₹${n?.toLocaleString("en-IN")}`;
};

const STATUS_COLORS = {
  approved: { bg: "#e8f5e9", color: "#1a6b3c", label: "✓ Live" },
  pending:  { bg: "#fff3e0", color: "#e65100", label: "⏳ Pending" },
  rejected: { bg: "#fde8e8", color: "#c0392b", label: "✕ Rejected" },
};

export default function DashboardPage() {
  const { user, profile, logout } = useAuth();
  const navigate = useNavigate();
  const { properties, loading, setProperties } = useMyProperties(user?.uid);
  const [deleting, setDeleting] = useState(null);
  const [tab, setTab] = useState("listings"); // listings | enquiries | profile

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this listing?")) return;
    setDeleting(id);
    try {
      await deleteProperty(id, user.uid);
      setProperties(prev => prev.filter(p => p.id !== id));
      toast.success("Listing deleted.");
    } catch {
      toast.error("Failed to delete. Try again.");
    } finally {
      setDeleting(null);
    }
  };

  const handleToggleUrgent = async (prop) => {
    try {
      await updateProperty(prop.id, { urgent: !prop.urgent });
      setProperties(prev => prev.map(p => p.id === prop.id ? { ...p, urgent: !p.urgent } : p));
      toast.success(`Urgent tag ${!prop.urgent ? "added" : "removed"}`);
    } catch {
      toast.error("Update failed.");
    }
  };

  const stats = {
    total:    properties.length,
    live:     properties.filter(p => p.status === "approved").length,
    pending:  properties.filter(p => p.status === "pending").length,
    views:    properties.reduce((s, p) => s + (p.views || 0), 0),
    enquiries:properties.reduce((s, p) => s + (p.enquiries || 0), 0),
  };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "20px 16px 40px" }}>
      {/* ── PROFILE HEADER ── */}
      <div style={{ background: "white", borderRadius: 16, padding: 20, border: "1px solid #e2ece2", marginBottom: 20, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ width: 60, height: 60, borderRadius: 30, background: "#1a6b3c", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {profile?.photoURL
            ? <img src={profile.photoURL} alt="" style={{ width: 60, height: 60, borderRadius: 30, objectFit: "cover" }} />
            : <span style={{ color: "white", fontWeight: 700, fontSize: 22 }}>{(profile?.name || user?.email || "U")[0].toUpperCase()}</span>
          }
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 18, color: "#1a2e1a" }}>{profile?.name || "My Account"}</h2>
          <p style={{ color: "#6b7c6b", fontSize: 13 }}>{user?.email} {profile?.phone && `· +91 ${profile.phone}`}</p>
          <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            {profile?.verified && <Pill bg="#e8f5e9" color="#1a6b3c">✓ Verified Seller</Pill>}
            <Pill bg="#fff3e0" color="#e65100">{stats.live} Active Listings</Pill>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => navigate("/add-property")} style={{ padding: "9px 16px", borderRadius: 8, background: "#1a6b3c", color: "white", border: "none", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
            + Add Listing
          </button>
          <button onClick={async () => { await logout(); navigate("/"); }} style={{ padding: "9px 14px", borderRadius: 8, background: "white", color: "#6b7c6b", border: "1px solid #e2ece2", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
            Logout
          </button>
        </div>
      </div>

      {/* ── STATS ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10, marginBottom: 24 }}>
        {[
          ["🏠", stats.total,     "Total Listings",  "#e8f5e9", "#1a6b3c"],
          ["✅", stats.live,      "Live",            "#e3f2fd", "#1565c0"],
          ["⏳", stats.pending,   "Pending",         "#fff3e0", "#e65100"],
          ["👁", stats.views,     "Total Views",     "#f3e5f5", "#6a1b9a"],
          ["✉️", stats.enquiries, "Enquiries",       "#e8f5e9", "#1a6b3c"],
        ].map(([icon, val, label, bg, color]) => (
          <div key={label} style={{ background: bg, borderRadius: 12, padding: "14px", textAlign: "center" }}>
            <div style={{ fontSize: 20, marginBottom: 2 }}>{icon}</div>
            <p style={{ fontSize: 22, fontWeight: 800, color, fontFamily: "'Sora',sans-serif" }}>{val}</p>
            <p style={{ fontSize: 11, color: "#6b7c6b", marginTop: 2 }}>{label}</p>
          </div>
        ))}
      </div>

      {/* ── TABS ── */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
        {[["listings","🏠 My Listings"],["enquiries","✉️ Enquiries"]].map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: "8px 16px", borderRadius: 20, fontWeight: 600, fontSize: 13, border: "1px solid #e2ece2",
            background: tab === t ? "#1a6b3c" : "white", color: tab === t ? "white" : "#1a2e1a", cursor: "pointer", fontFamily: "inherit"
          }}>{l}</button>
        ))}
      </div>

      {/* ── LISTINGS TAB ── */}
      {tab === "listings" && (
        loading ? <Spinner /> :
        properties.length === 0 ? (
          <div style={{ textAlign: "center", padding: "50px 20px", background: "white", borderRadius: 16, border: "1px solid #e2ece2" }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🏘️</div>
            <p style={{ fontWeight: 700, fontSize: 16, color: "#1a2e1a" }}>No listings yet</p>
            <p style={{ color: "#6b7c6b", fontSize: 14, marginBottom: 20 }}>Post your first property for free</p>
            <button onClick={() => navigate("/add-property")} style={{ padding: "11px 24px", background: "#1a6b3c", color: "white", border: "none", borderRadius: 9, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
              + Post Property
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {properties.map(p => {
              const statusStyle = STATUS_COLORS[p.status] || STATUS_COLORS.pending;
              const img = p.images?.[0] || "https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=300&q=70";
              return (
                <div key={p.id} style={{ background: "white", borderRadius: 12, border: "1px solid #e2ece2", overflow: "hidden", display: "flex", alignItems: "stretch" }}>
                  <img src={img} alt="" style={{ width: 100, height: 90, objectFit: "cover", flexShrink: 0 }} />
                  <div style={{ padding: "10px 14px", flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2, flexWrap: "wrap" }}>
                      <p style={{ fontWeight: 700, fontSize: 14, color: "#1a2e1a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 280 }}>{p.title}</p>
                      <span style={{ background: statusStyle.bg, color: statusStyle.color, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, flexShrink: 0 }}>{statusStyle.label}</span>
                    </div>
                    <p style={{ fontSize: 15, fontWeight: 800, color: "#1a6b3c", fontFamily: "'Sora',sans-serif" }}>{fmt(p.price)}</p>
                    <p style={{ fontSize: 12, color: "#6b7c6b" }}>📍 {p.location} · 👁 {p.views || 0} views · ✉️ {p.enquiries || 0} enquiries</p>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5, padding: "10px", justifyContent: "center", flexShrink: 0 }}>
                    <Link to={`/property/${p.id}`} style={{ background: "#e8f5e9", color: "#1a6b3c", fontSize: 12, fontWeight: 600, padding: "5px 10px", borderRadius: 6, textDecoration: "none", textAlign: "center" }}>
                      View
                    </Link>
                    <button onClick={() => handleToggleUrgent(p)} style={{ background: p.urgent ? "#fde8e8" : "#fff3e0", color: p.urgent ? "#c0392b" : "#e65100", fontSize: 12, fontWeight: 600, padding: "5px 10px", borderRadius: 6, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                      {p.urgent ? "🔥 Urgent" : "Mark Urgent"}
                    </button>
                    <button onClick={() => handleDelete(p.id)} disabled={deleting === p.id} style={{ background: "#fde8e8", color: "#c0392b", fontSize: 12, fontWeight: 600, padding: "5px 10px", borderRadius: 6, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                      {deleting === p.id ? "..." : "Delete"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── ENQUIRIES TAB ── */}
      {tab === "enquiries" && (
        <div style={{ background: "white", borderRadius: 16, padding: 24, border: "1px solid #e2ece2", textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>✉️</div>
          <p style={{ fontWeight: 700, fontSize: 15, color: "#1a2e1a" }}>Enquiries will appear here</p>
          <p style={{ fontSize: 13, color: "#6b7c6b", marginTop: 4 }}>When buyers send you messages, you'll see them in this tab.</p>
        </div>
      )}
    </div>
  );
}

function Pill({ children, bg, color }) {
  return <span style={{ background: bg, color, fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 20 }}>{children}</span>;
}
